/**
 * How much of the estate's routes are guarded, and how much was never read.
 *
 * Estate-level, with no single object to attach to — which is exactly what
 * pulled me into inventing a tab for it twice. It is not a destination; it is a
 * COVERAGE STATEMENT, the same family as `analyzer_coverage` ("did this check
 * run") and `scan_coverage` ("which files were read"). AnalysisDetailPage says
 * the rule for that family out loud: *"whether a check ran is a statement about
 * scan completeness, not about findings, so it lives with the file coverage."*
 *
 * It sits with the component inventory rather than in that per-analysis
 * coverage drawer, because the drawer describes ONE SCAN and this describes the
 * estate — `reach_query._current_analysis_ids` takes the latest scan per
 * repository. Putting estate figures on a per-scan surface is the mislabelling
 * this component was moved to avoid.
 *
 * Three numbers, and the third is the point
 * -----------------------------------------
 * A figure with two buckets is a score, and a score invites a target. The third
 * says how much of the denominator could not be read. The percentage is taken
 * over routes that were READ, so it never appears without that denominator, and
 * `null` renders as an absence rather than as 0%.
 *
 * And `reading` is rendered rather than dropped, because the number invites the
 * opposite conclusion: controls applied through a shared helper are resolved one
 * call hop out and no further, so an estate that centralises its safety
 * configuration — the practice a model-risk function asks for — is the kind this
 * sees least of.
 */
import {
  Accordion, AccordionDetails, AccordionSummary, Alert, AlertTitle, Box,
  Card, CardContent, Chip, Grid, LinearProgress, Tooltip, Typography,
} from '@mui/material'
import { ExpandMore, HelpOutline } from '@mui/icons-material'
import { GuardrailCoverage as Coverage } from '../services/api'
import { ROUTE_MEANING } from './exposureVerdicts'

const BUCKET: Record<string, { label: string; color: string; title: string }> = {
  guarded: {
    label: 'guarded',
    color: 'info.main',
    title: 'Every hop on the route was examined and carried a control.',
  },
  unguarded: {
    label: 'unguarded',
    color: 'error.main',
    title: 'Every hop was examined and none carried a control.',
  },
  unknown: {
    label: 'not read',
    color: 'text.disabled',
    title:
      'At least one hop’s scope was never read, so the route is unknown rather than unguarded. Not evidence either way.',
  },
}

const GuardrailCoverage = ({ coverage }: { coverage: Coverage }) => (
  <Card variant="outlined" sx={{ mb: 2 }}>
    <CardContent>
      <Typography variant="h6">
        Are your inbound routes validated?
      </Typography>
      {/* The SUBJECT first. The heading was "Guardrail coverage" over a
          sentence about scope — "across every repository… not a snapshot of
          any one scan" — which answers a question nobody has yet, because
          coverage OF WHAT had not been said. */}
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
        {ROUTE_MEANING}
      </Typography>
      {/* And the relationship to the table underneath, which is the same data
          one row at a time. A reader seeing two unexplained things is worse
          off than one seeing a thing and its summary. */}
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
        These are the <strong>Route verdict</strong> values below, counted —
        across every repository scanned for this tenant, as the estate stands
        now, not a snapshot of any one scan.
      </Typography>

      <Grid container spacing={2} sx={{ mb: 2 }}>
        {(['guarded', 'unguarded', 'unknown'] as const).map((bucket) => (
          <Grid item xs={4} key={bucket}>
            <Tooltip title={BUCKET[bucket].title}>
              <Box>
                <Typography variant="caption" color="text.secondary">
                  {BUCKET[bucket].label}
                </Typography>
                <Typography variant="h5" sx={{ color: BUCKET[bucket].color }}>
                  {coverage.counts[bucket]}
                </Typography>
              </Box>
            </Tooltip>
          </Grid>
        ))}
      </Grid>

      {coverage.guarded_pct_of_read === null ? (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
          No route&rsquo;s controls could be read, so there is no coverage figure
          to report. That is not 0% — it is an absence.
        </Typography>
      ) : (
        <>
          <LinearProgress
            variant="determinate"
            value={coverage.guarded_pct_of_read}
            sx={{ height: 8, borderRadius: 4, mb: 0.5 }}
          />
          <Typography variant="body2" sx={{ mb: 1 }}>
            <strong>{coverage.guarded_pct_of_read}%</strong> of the{' '}
            <strong>{coverage.routes_read}</strong> route
            {coverage.routes_read === 1 ? '' : 's'} whose controls could be read
            {coverage.routes_unread > 0 && (
              <>
                {' '}— <strong>{coverage.routes_unread}</strong> more could not
                be read at all
              </>
            )}
            .
          </Typography>
        </>
      )}

      {coverage.controls_in_use.length > 0 && (
        <Box sx={{ display: 'flex', gap: 0.75, flexWrap: 'wrap', mb: 1.5 }}>
          {coverage.controls_in_use.map((c) => (
            <Chip
              key={c.control}
              size="small"
              variant="outlined"
              label={`${c.control.replace(/_/g, ' ')} · ${c.routes}`}
            />
          ))}
        </Box>
      )}

      {/* What to do, first. The caveat about detection depth is real and is
          not the headline — it was the whole of this box, under a title that
          promised help and delivered an account of our own internals. */}
      <Alert severity="info" icon={<HelpOutline />} sx={{ mb: 1 }}>
        <AlertTitle sx={{ fontSize: '0.875rem' }}>Where to start</AlertTitle>
        <Typography variant="body2">{coverage.reading}</Typography>
      </Alert>

      {coverage.limit && (
        <Accordion disableGutters elevation={0} sx={{ '&:before': { display: 'none' } }}>
          <AccordionSummary expandIcon={<ExpandMore />} sx={{ px: 0, minHeight: 0 }}>
            <Typography variant="caption" color="text.secondary">
              Why the guarded figure can understate
            </Typography>
          </AccordionSummary>
          <AccordionDetails sx={{ px: 0, pt: 0 }}>
            <Typography variant="caption" color="text.secondary">
              {coverage.limit}
            </Typography>
          </AccordionDetails>
        </Accordion>
      )}
    </CardContent>
  </Card>
)

export default GuardrailCoverage
